import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Menu, MenuItem } from '@material-ui/core';
import ConceptLinkerValidatorItemsModel from 'models/ConceptLinkerValidatorItemsModel';
import { ConceptLinkerValidatorModel } from 'models/ConceptLinkerValidatorModel';
import { IConceptLinkerValidatorItem } from 'types/conceptLinkerValidator';
import { ConceptLinkerValidatorItemStatus } from 'constants/conceptLinkerValidatorStatus';

interface ConceptLinkerValidatorBulkActionsProps {
  itemsModel: ConceptLinkerValidatorItemsModel;
  conceptLinkerValidatorModel: ConceptLinkerValidatorModel;
  onActionComplete: () => void;
}

const ConceptLinkerValidatorBulkActions: React.FC<ConceptLinkerValidatorBulkActionsProps> = ({
  itemsModel,
  conceptLinkerValidatorModel,
  onActionComplete,
}) => {
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);

  // Calculate bulk action counts
  const { bulkKeepAllCount, bulkChangeCount, bulkUnlinkCount } = useMemo(() => {
    if (!itemsModel.gridData || !Array.isArray(itemsModel.gridData)) {
      return { bulkKeepAllCount: 0, bulkChangeCount: 0, bulkUnlinkCount: 0 };
    }

    const items = itemsModel.gridData as IConceptLinkerValidatorItem[];

    // Count items that need review by AI decision
    const changeCount = items.filter(
      (item) =>
        item.status === ConceptLinkerValidatorItemStatus.NEEDS_USER_REVIEW && item.ai_result?.decision === 'change',
    ).length;

    const unlinkCount = items.filter(
      (item) =>
        item.status === ConceptLinkerValidatorItemStatus.NEEDS_USER_REVIEW && item.ai_result?.decision === 'unlink',
    ).length;

    // Keep All count = all items needing review (change + unlink suggestions)
    const keepAllCount = changeCount + unlinkCount;

    return { bulkKeepAllCount: keepAllCount, bulkChangeCount: changeCount, bulkUnlinkCount: unlinkCount };
  }, [itemsModel.gridData]);

  const handleBulkMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setBulkMenuAnchor(event.currentTarget);
  };

  const handleBulkMenuClose = () => {
    setBulkMenuAnchor(null);
  };

  const handleBulkAction = async (action: 'keep' | 'change' | 'unlink') => {
    handleBulkMenuClose();
    if (isProcessingBulk) return;

    setIsProcessingBulk(true);
    try {
      const result = 
        action === 'keep' ? await itemsModel.bulkKeep() :
        action === 'change' ? await itemsModel.bulkChange() :
        await itemsModel.bulkUnlink();

      if (result.data) {
        // Refresh the conceptLinkerValidatorModel to update summary
        await conceptLinkerValidatorModel.reload();
        onActionComplete();
      }
    } catch (error) {
      console.error(`Error during bulk ${action}:`, error);
    } finally {
      setIsProcessingBulk(false);
    }
  };

  // Don't render if no actions available
  if (bulkKeepAllCount === 0) {
    return null;
  }

  return (
    <>
      <BulkActionsButton 
        onClick={handleBulkMenuClick} 
        disabled={isProcessingBulk || itemsModel.loadingGrid}
      >
        {isProcessingBulk ? 'Processing...' : 'Bulk Actions'}
      </BulkActionsButton>
      <Menu 
        anchorEl={bulkMenuAnchor} 
        open={Boolean(bulkMenuAnchor)} 
        onClose={handleBulkMenuClose}
      >
        <MenuItem 
          onClick={() => handleBulkAction('keep')} 
          disabled={bulkKeepAllCount === 0}
        >
          Keep All Needs Review ({bulkKeepAllCount})
        </MenuItem>
        <MenuItem 
          onClick={() => handleBulkAction('change')} 
          disabled={bulkChangeCount === 0}
        >
          Apply All Changes ({bulkChangeCount})
        </MenuItem>
        <MenuItem 
          onClick={() => handleBulkAction('unlink')} 
          disabled={bulkUnlinkCount === 0}
        >
          Unlink All ({bulkUnlinkCount})
        </MenuItem>
      </Menu>
    </>
  );
};

export default ConceptLinkerValidatorBulkActions;

const BulkActionsButton = styled.button`
  padding: 8px 16px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s;

  &:hover:not(:disabled) {
    background-color: #45a049;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;